import { Controller } from "@hotwired/stimulus"

// Drag-and-drop + multi-file upload progress for Active Storage direct uploads. The file input
// itself carries `direct_upload: true` (and usually `multiple: true`); @rails/activestorage does
// the actual presigned PUTs. This controller only adds the drop zone and a per-file progress row,
// listening to the DOM events Active Storage dispatches on the input as it uploads.
//
// Targets: input (the file_field), dropzone (optional highlightable area), progress (list container).
// Set data-direct-upload-autosubmit-value="true" to submit the form as soon as files are chosen.
export default class extends Controller {
  static targets = ["input", "dropzone", "progress"]
  static values = { autosubmit: Boolean, maxSize: Number }

  connect() {
    // Active Storage events bubble up from the input to this element.
    this.element.addEventListener("direct-upload:initialize", this.onInitialize)
    this.element.addEventListener("direct-upload:progress", this.onProgress)
    this.element.addEventListener("direct-upload:error", this.onError)
    this.element.addEventListener("direct-upload:end", this.onEnd)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:initialize", this.onInitialize)
    this.element.removeEventListener("direct-upload:progress", this.onProgress)
    this.element.removeEventListener("direct-upload:error", this.onError)
    this.element.removeEventListener("direct-upload:end", this.onEnd)
  }

  // --- drag & drop ---
  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.add("border-primary")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.remove("border-primary")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.remove("border-primary")
    if (!this.hasInputTarget || event.dataTransfer.files.length === 0) return
    this.inputTarget.files = event.dataTransfer.files
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Triggered by data-action change->direct-upload#selected on the input.
  selected() {
    if (this.autosubmitValue) this.element.requestSubmit?.()
  }

  // Fail-fast size check on selection/drop. Active Storage only enforces the cap server-side
  // *after* the (possibly multi-GB) direct upload finishes, so reject oversized files here before
  // any bytes leave the browser. Clears stale rows first; rejects the batch and empties the input.
  validateSize() {
    if (!this.hasInputTarget) return
    if (this.hasProgressTarget) this.progressTarget.innerHTML = ""
    if (!this.maxSizeValue) return

    const tooBig = Array.from(this.inputTarget.files).filter((file) => file.size > this.maxSizeValue)
    if (tooBig.length === 0) return

    this.inputTarget.value = "" // nothing gets uploaded on submit
    if (!this.hasProgressTarget) return
    tooBig.forEach((file) => {
      const row = document.createElement("div")
      row.className = "text-sm font-medium text-error"
      row.textContent = `${file.name} – ✗ ${this.tooLargeMessage}`
      this.progressTarget.appendChild(row)
    })
  }

  // --- progress rendering ---
  onInitialize = (event) => {
    const { id, file } = event.detail
    if (!this.hasProgressTarget) return
    const row = document.createElement("div")
    row.id = `direct-upload-${id}`
    row.className = "text-sm opacity-70"
    row.textContent = `${file.name} … 0 %`
    this.progressTarget.appendChild(row)
  }

  onProgress = (event) => {
    const { id, progress } = event.detail
    const row = document.getElementById(`direct-upload-${id}`)
    if (row) row.textContent = row.textContent.replace(/\d+ %$/, `${Math.round(progress)} %`)
  }

  onError = (event) => {
    event.preventDefault()
    this.errored ||= new Set()
    this.errored.add(event.detail.id)
    const row = document.getElementById(`direct-upload-${event.detail.id}`)
    if (!row) return
    row.classList.remove("opacity-70")
    row.classList.add("text-error", "font-medium")
    const name = event.detail.file?.name ? `${event.detail.file.name} – ` : ""
    row.textContent = `${name}✗ ${this.errorMessage}`
  }

  // `direct-upload:end` fires after both success and error; only mark ✓ when it actually succeeded.
  onEnd = (event) => {
    if (this.errored?.has(event.detail.id)) return
    const row = document.getElementById(`direct-upload-${event.detail.id}`)
    if (row) row.textContent = row.textContent.replace(/… \d+ %$/, "✓")
  }

  get errorMessage() {
    return this.progressTarget.dataset.errorMessage
  }

  get tooLargeMessage() {
    return this.progressTarget.dataset.tooLargeMessage
  }
}
